import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD } from '../../../shared/format.js';
import { BarcodeField } from './BarcodeField.jsx';
import { EmojiPicker } from './EmojiPicker.jsx';

const TYPES = [
  { key: 'sellable', label: 'قابل للبيع' },
  { key: 'consumable', label: 'استهلاكي' },
  { key: 'service', label: 'خدمة' },
  { key: 'gift', label: 'هدية' }
];

const UNITS = [
  { key: 'piece', label: 'قطعة' },
  { key: 'kg', label: 'كيلو' },
  { key: 'liter', label: 'لتر' },
  { key: 'pack', label: 'علبة' },
  { key: 'box', label: 'صندوق' }
];

function defaults(p) {
  return {
    name: p?.name || '',
    emoji: p?.emoji || '',
    barcode: p?.barcode || '',
    sku: p?.sku || '',
    warehouseId: p?.warehouseId?._id || p?.warehouseId || '',
    categoryId: p?.categoryId?._id || p?.categoryId || '',
    type: p?.type || 'sellable',
    unit: p?.unit || 'piece',
    individualBarcode: !!p?.individualBarcode,
    // الخدمات افتراضياً بلا مخزون
    tracksStock: p ? p.tracksStock !== false : true,
    costPrice: p?.costPrice ?? 0,
    salePrice: p?.salePrice ?? 0,
    currentStock: p?.currentStock ?? 0,
    minStockAlert: p?.minStockAlert ?? 5,
    description: p?.description || '',
    isActive: p?.isActive !== false
  };
}

export function ProductDrawer({
  open,
  product,
  warehouses = [],
  categories = [],
  onClose,
  onSubmit,
  submitting,
  saveAndNew = false
}) {
  const isEdit = !!product;
  const [form, setForm] = useState(defaults(product));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(defaults(product));
      setErrors({});
    }
  }, [open, product]);

  const wh = form.warehouseId;
  const cats = useMemo(() => categories.filter((c) => String(c.warehouseId?._id || c.warehouseId) === String(wh)), [categories, wh]);

  const margin = useMemo(() => {
    const c = Number(form.costPrice) || 0;
    const s = Number(form.salePrice) || 0;
    if (c <= 0) return 0;
    return Math.round(((s - c) / c) * 100);
  }, [form.costPrice, form.salePrice]);

  if (!open) return null;

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function validate() {
    const e = {};
    if (!form.name?.trim()) e.name = 'اسم المنتج مطلوب';
    if (!form.warehouseId) e.warehouseId = 'المخزن مطلوب';
    if (!form.categoryId) e.categoryId = 'القسم مطلوب';
    if (Number(form.salePrice) < 0) e.salePrice = 'سعر البيع غير صالح';
    if (Number(form.costPrice) < 0) e.costPrice = 'سعر الكلفة غير صالح';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave(continueAfter = false) {
    if (!validate()) return;
    const body = {
      name: form.name.trim(),
      emoji: form.emoji || null,
      warehouseId: form.warehouseId,
      categoryId: form.categoryId,
      type: form.type,
      tracksStock: !!form.tracksStock,
      unit: form.unit,
      individualBarcode: !!form.individualBarcode,
      costPrice: Math.round(Number(form.costPrice) || 0),
      salePrice: Math.round(Number(form.salePrice) || 0),
      minStockAlert: Math.round(Number(form.minStockAlert) || 0),
      isActive: !!form.isActive,
      description: form.description || ''
    };
    if (form.barcode) body.barcode = form.barcode.trim();
    if (!isEdit) {
      body.currentStock = Math.round(Number(form.currentStock) || 0);
    }
    onSubmit?.(body, { continueAfter });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch"
      style={{ background: 'rgba(0,0,0,.4)' }}
    >
      <div className="flex-1" />
      <div
        className="bg-white border-s border-line w-[460px] max-w-full h-full flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h flex items-center">
          <h3 className="text-[15px] font-bold flex-1">
            {isEdit ? 'تعديل منتج' : 'منتج جديد'}
          </h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto card-b space-y-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الأيقونة / الإيموجي</label>
            <EmojiPicker value={form.emoji} onChange={(v) => setField('emoji', v)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">اسم المنتج *</label>
            <input
              className={clsx('input', errors.name && 'border-red')}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="مثلاً: برغر دجاج"
            />
            {errors.name ? <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.name}</div> : null}
          </div>

          <BarcodeField
            value={form.barcode}
            onChange={(v) => setField('barcode', v)}
            error={errors.barcode}
            productName={form.name}
            sku={form.sku}
            price={Number(form.salePrice) || 0}
          />

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">SKU</label>
            <input
              className="input"
              value={form.sku}
              dir="ltr"
              style={{ fontFamily: 'monospace', textAlign: 'right' }}
              readOnly={isEdit}
              placeholder={isEdit ? '' : 'تلقائي عند الحفظ'}
              onChange={(e) => setField('sku', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">المخزن *</label>
              <select
                className={clsx('input', errors.warehouseId && 'border-red')}
                value={form.warehouseId}
                onChange={(e) => setField('warehouseId', e.target.value)}
              >
                <option value="">— اختر —</option>
                {warehouses.map((w) => (
                  <option key={w._id || w.id} value={w._id || w.id}>{w.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">القسم *</label>
              <select
                className={clsx('input', errors.categoryId && 'border-red')}
                value={form.categoryId}
                onChange={(e) => setField('categoryId', e.target.value)}
                disabled={!form.warehouseId}
              >
                <option value="">— اختر —</option>
                {cats.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">نوع المنتج</label>
            <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    setField('type', t.key);
                    // «خدمة» تعني بلا مخزون افتراضياً، ويمكن للمستخدم تغييرها.
                    setField('tracksStock', t.key !== 'service');
                  }}
                  className={clsx(
                    'flex-1 px-2 py-1.5 rounded-[8px] text-[12px] font-semibold transition-colors',
                    form.type === t.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <label className="flex items-start gap-2 cursor-pointer mt-2 p-2 rounded-[10px] bg-paper-2">
              <input
                type="checkbox"
                className="accent-orange mt-0.5"
                checked={!form.tracksStock}
                onChange={(e) => setField('tracksStock', !e.target.checked)}
              />
              <span className="text-[12.5px] text-ink-2 leading-relaxed">
                <b>يُباع بدون مخزون</b>
                <span className="block text-[11.5px] text-ink-3">
                  للخدمات والبنود غير المخزنية: يُباع دائماً بلا خصم كمية، ولا يظهر في
                  تنبيه نقص المخزون، ولا يمكن شراؤه ضمن فاتورة مخزنية.
                </span>
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">وحدة القياس</label>
              <select
                className="input"
                value={form.unit}
                onChange={(e) => setField('unit', e.target.value)}
              >
                {UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">باركود إفرادي</label>
              <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
                <button
                  type="button"
                  onClick={() => setField('individualBarcode', false)}
                  className={clsx(
                    'flex-1 px-2 py-1.5 rounded-[8px] text-[12px] font-semibold',
                    !form.individualBarcode ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                  )}
                >
                  لا
                </button>
                <button
                  type="button"
                  onClick={() => setField('individualBarcode', true)}
                  className={clsx(
                    'flex-1 px-2 py-1.5 rounded-[8px] text-[12px] font-semibold',
                    form.individualBarcode ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                  )}
                >
                  نعم
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">سعر الكلفة (د.ع)</label>
              <input
                className={clsx('input', errors.costPrice && 'border-red')}
                type="number"
                min="0"
                value={form.costPrice}
                onChange={(e) => setField('costPrice', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">سعر البيع (د.ع) *</label>
              <input
                className={clsx('input', errors.salePrice && 'border-red')}
                type="number"
                min="0"
                value={form.salePrice}
                onChange={(e) => setField('salePrice', e.target.value)}
                style={{ fontWeight: 700 }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between bg-paper-2 rounded-[10px] px-3 py-2">
            <span className="text-[12px] text-ink-2">الهامش</span>
            <Pill variant={margin > 50 ? 'success' : margin > 20 ? 'warning' : 'danger'}>
              {margin}%
            </Pill>
          </div>

          <div className="grid grid-cols-2 gap-3" hidden={!form.tracksStock}>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">
                الكمية الحالية
                {isEdit ? <span className="text-ink-3 ms-1">(للتسوية استخدم شاشة المخزون)</span> : null}
              </label>
              <input
                className="input"
                type="number"
                min="0"
                value={form.currentStock}
                readOnly={isEdit}
                onChange={(e) => setField('currentStock', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الحد الأدنى للتنبيه</label>
              <input
                className="input"
                type="number"
                min="0"
                value={form.minStockAlert}
                onChange={(e) => setField('minStockAlert', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الوصف</label>
            <textarea
              className="input"
              rows={2}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="اختياري"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2 pt-1">
            <input
              type="checkbox"
              className="accent-orange"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
            />
            فعّال
          </label>

          {form.salePrice > 0 && form.costPrice > 0 ? (
            <div className="bg-paper-2 rounded-[10px] p-2.5 text-[12px] text-ink-2 flex items-center justify-between">
              <span>ربح بالقطعة</span>
              <b>{formatIQD(Math.max(0, form.salePrice - form.costPrice))}</b>
            </div>
          ) : null}
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>إلغاء</button>
          {!isEdit && saveAndNew ? (
            <button
              type="button"
              className="btn"
              onClick={() => handleSave(true)}
              disabled={submitting}
            >
              حفظ وإضافة جديد
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleSave(false)}
            disabled={submitting}
          >
            <Icon name="check" size={14} />
            {submitting ? 'جاري الحفظ...' : isEdit ? 'حفظ' : 'إنشاء المنتج'}
          </button>
        </div>
      </div>
    </div>
  );
}
